import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { spots, transfer } from '../../services/investmentCalculations';
import {
  calculateImpressions,
  contentDisplayIsSuspended,
  getCpmiByStatusCampaign,
  getDisplayDiscounts,
  getMultiplyByStatus,
  getRelationShowDays,
  getSpotsPerDay,
} from '../../../../../services/display';

const DECIMAL = 8;
const DECIMAL_FOR_CHECK = 4;
// Tolerance (in spots) when converting accumulated credit back into spots.
const SPOT_EPSILON = 1e-6;

/**
 * useBudgetTransfer
 *
 * Manages all state and logic for the BudgetTransfer modal (ImpressionManagementComponent).
 *
 * ## Reuses from investmentCalculations.js:
 *   - spots.fromBudget  — used in updateBudget and updateImpressionsToMax
 *   - spots.toBudget    — used in updateImpressions and calculateBudget
 *   - transfer.calcCredit       — extracts credit from origin contents (handleTransfer)
 *   - transfer.distributeCredit — distributes credit to destinations (handleTransfer)
 *   - transfer.minSpotsForRecord — enforces discount floor on input fields
 *   - transfer.diff             — computes added/subtracted spot diffs
 *
 * ## Why useInvestmentManager is NOT reused:
 *   That hook dispatches changes immediately to Redux.
 *   BudgetTransfer uses a preview-then-commit pattern:
 *   - All changes during the modal session are stored in local state (contentsDisplaysSelect)
 *   - Changes are committed to Redux/parent ONLY when the user clicks "Apply changes"
 *     via updateImpressionsCustomContents → updateImpressionsDisplay (prop)
 *
 * @param {object}   params
 * @param {object}   params.currentCampaign
 * @param {object}   params.reportSingleData
 * @param {Array}    params.customContents
 * @param {boolean}  params.transferModalVisible
 * @param {Array}    params.cart
 * @param {function} params.updateImpressionsDisplay - Parent callback to commit changes
 * @param {function} params.showTransferModal        - Parent callback to close the modal
 * @param {boolean}  params.editCampaignActive       - Needed to resolve the historical multiplier/CPMi
 */
function useBudgetTransfer({
  currentCampaign,
  reportSingleData,
  customContents,
  transferModalVisible,
  cart,
  updateImpressionsDisplay,
  showTransferModal,
  editCampaignActive,
}) {
  const { t } = useTranslation();
  const [contentsDisplaysSelect, setContentsDisplaysSelect] = useState([]);
  const [customContentsAux, setCustomContentsAux] = useState(customContents);
  const [view, setView] = useState('ad');
  const [displayOptions, setDisplayOptions] = useState(cart);
  const [displayFilter, setDisplayFilter] = useState(['all']);
  const [creditAvailable, setCreditAvailable] = useState(0);
  const [subsidizedCreditAvailable, setSubsidizedCreditAvailable] = useState(0);
  const [adVersions, setAdVersions] = useState([]);
  const [originVersion, setOriginVersion] = useState(undefined);
  const [destinationVersion, setDestinationVersion] = useState(undefined);
  const [originScreen, setOriginScreen] = useState(undefined);
  const [destinationScreen, setDestinationScreen] = useState(undefined);
  const [transferPercentage, setTransferPercentage] = useState(100);

  const format = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currentCampaign.currency,
    minimumFractionDigits: 3,
  });

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  const setCreditAvailableFormat = (value) => {
    const factor = Math.pow(10, DECIMAL);
    setCreditAvailable(Math.round(value * factor) / factor);
  };

  const setSubsidizedCreditAvailableFormat = (value) => {
    const factor = Math.pow(10, DECIMAL);
    setSubsidizedCreditAvailable(Math.round(value * factor) / factor);
  };


  const spotsAffordable = (credit, cpm) => {
    const costPerImpression = cpm / 1000;
    if (costPerImpression <= 0 || credit <= 0) return 0;
    return Math.floor(credit / costPerImpression + SPOT_EPSILON);
  };

  const calculateImpressionsView = useCallback((relation) => {
    if (relation.length < 1) return 0;
    if (!relation.content) return relation.promised_shows;
    return calculateImpressions(
      'to_consumed_not_subsidized',
      relation,
      reportSingleData,
      { id: relation.displayId },
    );
  }, [reportSingleData]);

  const calculateSubsidizedShows = (relation) => {
    if (relation.length < 1) return 0;
    if (!relation.content) return relation.promised_shows_subsidized;
    return calculateImpressions(
      'to_consumed_subsidized',
      relation,
      reportSingleData,
      { id: relation.displayId },
    );
  }

  const refreshCustomContentsAux = (updatedContentsDisplaysSelect) => {
    const flat = updatedContentsDisplaysSelect.flatMap(g => g.customContents);
    setCustomContentsAux(prev =>
      prev.map(item => flat.find(cc => cc.relationId === item.relationId) || item),
    );
  };

  const groupByCompany = (displayList) =>
    Object.values(
      displayList.reduce((acc, display) => {
        const company_id = display.company_id || 'Not assigned';
        if (!acc[company_id]) {
          acc[company_id] = { key: display.company.name, companyId: company_id, displays: [] };
        }
        acc[company_id].displays.push(display);
        return acc;
      }, {}),
    );

  const groupViewData = (displayList) => {
    const groupedDisplays = groupByCompany(displayList);
    return groupedDisplays.map(group => {
      const displayIdList = displayList
        .filter(d => d.company_id === group.companyId)
        .map(d => d.id);
      const filteredContents = customContentsAux.filter(item => displayIdList.includes(item.displayId));

      const updatedContents = filteredContents.map(item => {
        const matchingDisplay =
          currentCampaign.contents_displays.find(d => d.display_id === item.displayId && d.cpm) ||
          currentCampaign.contents_displays.find(d => d.display_id === item.displayId);
        const cartDisplay = cart.find(d => d.id === item.displayId);

        const appliedDiscount = item.programmatic_volume_discount_apply || null;
        const hasVolumeDiscounts = !!getDisplayDiscounts(cartDisplay);
        const relationShowDays = getRelationShowDays(
          currentCampaign.start_date,
          currentCampaign.end_date,
          item.rules,
        );
        const consumedImpressions = calculateImpressionsView(item);
        const spotsPerDay = getSpotsPerDay(
          { ...item, promised_shows: consumedImpressions },
          currentCampaign.start_date,
          currentCampaign.end_date,
        );

        const smartCampaignCpm =
          (matchingDisplay && matchingDisplay.cpm) ||
          item.cpm ||
          (cartDisplay && cartDisplay.smart_campaign_cpm) ||
          0;

        // The modal keeps operating on spots: multiplier and CPMi are display-only.
        // CPMi goes through the same helper as the relation slider so both show the same value.
        const displayForCpm = cartDisplay || {
          id: item.displayId,
          smart_campaign_cpm: smartCampaignCpm,
        };
        const multiplier = getMultiplyByStatus(editCampaignActive, currentCampaign, displayForCpm);
        const cpmi = getCpmiByStatusCampaign(editCampaignActive, currentCampaign, displayForCpm);

        return {
          ...item,
          display_name:
            (matchingDisplay && matchingDisplay.display_name) ||
            (cartDisplay && cartDisplay.name) ||
            '',
          smart_campaign_cpm: smartCampaignCpm,
          multiplier,
          cpmi,
          content_version_name: item.content.content_version_name || 'V1',
          appliedDiscount,
          hasVolumeDiscounts,
          spotsPerDay,
          relationShowDays,
        };
      });

      return { companyId: group.companyId, key: group.key, customContents: updatedContents };
    });
  };

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const displayList = displayFilter.includes('all')
      ? cart
      : cart.filter(item => displayFilter.includes(item.id));

    const viewData = groupViewData(displayList);

    setContentsDisplaysSelect(viewData);

    const versions = new Set();
    customContentsAux.forEach(cc => {
      if (cc.content.content_version_name) versions.add(cc.content.content_version_name);
    });
    setAdVersions(Array.from(versions));
  }, [transferModalVisible, view, displayFilter, cart, customContentsAux]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleRadioChange = (e) => setView(e.target.value);

  const handleChangeDisplayFilter = (value) => {
    if (value.includes('all') && value.length > 1) {
      setDisplayFilter(displayFilter.includes('all') ? value.filter(v => v !== 'all') : ['all']);
      return;
    }
    setDisplayFilter(value.length === 0 ? ['all'] : value);
  };

  const handleSearchDisplayFilter = (value) => {
    setDisplayOptions(
      value === ''
        ? cart
        : cart.filter(item => item.name.toLowerCase().includes(value.toLowerCase())),
    );
  };

  const getCurrentShows = (content) => {
    if (content.promised_shows_update !== undefined) return content.promised_shows_update;
    return Math.max(0, calculateImpressionsView(content));
  };

  const getCurrentSubsidizedShows = (content) => {
    if (content.promised_shows_subsidized_update === 0 || content.promised_shows_subsidized_update !== undefined) {
      return parseInt(content.promised_shows_subsidized_update);
    }
    return parseInt(calculateSubsidizedShows(content));
  };

  const getDisplayForItem = useCallback(
    (item) => cart.find(d => d.id === item.displayId) || null,
    [cart],
  );

  const getPaidConsumed = useCallback((item) => {
    if (!item) return 0;
    const total = item.promised_shows || 0;
    const remaining = Math.max(0, calculateImpressionsView(item));
    return Math.max(0, total - remaining);
  }, [calculateImpressionsView]);

  const calcCreditOptions = {
    getCpm: (content) => content.smart_campaign_cpm,
    getValue: getCurrentShows,
    getMinSpotsToKeep: (displayOrigins) => {
      const first = displayOrigins[0];
      const hasExcluded = displayOrigins.some(
        o => o.revision_status === 'out_of_service' || o.revision_status === 'rejected',
      );
      const days = (first && first.relationShowDays) || 0;
      if (hasExcluded || !first || days <= 0) return 0;

      const display = {
        minimum_programmatic_spots: first.minimum_programmatic_spots,
        minimum_programmatic_period: first.minimum_programmatic_period,
      };
      const floor = transfer.displayExtractionFloor(display, first.appliedDiscount, days);
      if (floor <= 0) return 0;

      const consumedTotal = displayOrigins.reduce((sum, o) => sum + getPaidConsumed(o), 0);
      return Math.max(0, floor - consumedTotal);
    },
  };

  /**
   * Transfer impressions between ad versions or screens.
   *
   * "By Ad" mode: operates per-display — each screen transfers within itself.
   *   - Case 1: screen has both originVersion and destinationVersion → transfer within that screen.
   *   - Case 2: screen has originVersion but NOT destinationVersion → show a warning toast and skip.
   *
   * "By Display" mode: transfers all contents from one screen to another (unchanged).
   *
   * Uses transfer.calcCredit to extract from origins (respecting discount floors)
   * and transfer.distributeCredit to assign to destinations.
   */
  /**
   * Given the origin impression changes computed by transfer.calcCredit, extracts
   * subsidized shows proportionally from each origin and returns the total extracted.
   */
  const extractSubsidizedFromOrigins = (originContents, originChanges, subsidizedChanges) => {
    let totalSubsidizedCredit = 0;
    originChanges.forEach((newPaidShows, relationId) => {
      const item = originContents.find(c => c.relationId === relationId);
      if (!item) return;
      const currentPaidShows = getCurrentShows(item);
      const currentSubsidizedShows = getCurrentSubsidizedShows(item);
      if (currentPaidShows > 0 && currentSubsidizedShows > 0) {
        const subsidizedDelta = Math.round(((currentPaidShows - newPaidShows) / currentPaidShows) * currentSubsidizedShows);
        if (subsidizedDelta > 0) {
          subsidizedChanges.set(relationId, currentSubsidizedShows - subsidizedDelta);
          totalSubsidizedCredit += spots.toBudget(subsidizedDelta, item.smart_campaign_cpm);
        }
      }
    });
    return totalSubsidizedCredit;
  };

  /**
   * Distributes totalSubsidizedCredit (in currency units) equally among destinations,
   * mirroring distributeTransferCredit from investmentCalculations.
   */
  const distributeSubsidizedToDestinations = (destContents, destChanges, totalSubsidizedCredit, subsidizedChanges) => {
    if (totalSubsidizedCredit <= 0) return;
    const creditPerDest = totalSubsidizedCredit / destChanges.size;
    destChanges.forEach((_, relationId) => {
      const item = destContents.find(c => c.relationId === relationId);
      if (!item) return;
      const subsidizedAdded = spots.fromBudget(creditPerDest, item.smart_campaign_cpm);
      if (subsidizedAdded > 0) {
        subsidizedChanges.set(relationId, getCurrentSubsidizedShows(item) + subsidizedAdded);
      }
    });
  };

  const handleTransfer = () => {
    if (!transferPercentage) return;

    const impressionChanges = new Map();
    const subsidizedChanges = new Map();

    if (view === 'ad') {
      const allContents = contentsDisplaysSelect.flatMap(g => g.customContents);
      const displayIds = [...new Set(allContents.map(c => c.displayId))];

      displayIds.forEach(displayId => {
        const displayContents = allContents.filter(c => c.displayId === displayId);
        const originContents = displayContents.filter(
          c => c.content && c.content.content_version_name === originVersion,
        );

        // This display doesn't have the origin version — not involved in this transfer
        if (originContents.length === 0) return;

        const destContents = displayContents.filter(
          c => c.content && c.content.content_version_name === destinationVersion && !contentDisplayIsSuspended(c),
        );

        if (destContents.length === 0) {
          const displayName = (displayContents[0] && displayContents[0].display_name) || `Display ${displayId}`;
          message.warning(t('Display {{displayName}} does not have {{version}}. Transfer was not applied.', { displayName, version: destinationVersion }));
          return;
        }

        const { totalCredit, impressionChanges: originChanges } = transfer.calcCredit(
          originContents,
          transferPercentage,
          calcCreditOptions,
        );

        originChanges.forEach((value, key) => impressionChanges.set(key, value));
        const totalSubsidizedCredit = extractSubsidizedFromOrigins(originContents, originChanges, subsidizedChanges);

        if (totalCredit > 0) {
          const destChanges = transfer.distributeCredit(totalCredit, destContents, {
            getCpm: (content) => content.smart_campaign_cpm,
            getValue: getCurrentShows,
          });
          destChanges.forEach((value, key) => impressionChanges.set(key, value));
          distributeSubsidizedToDestinations(destContents, destChanges, totalSubsidizedCredit, subsidizedChanges);
        }
      });
    } else {
      // "By Display" mode: global transfer between two specific screens
      const allOriginContents = [];
      const allDestinationContents = [];

      contentsDisplaysSelect.forEach(group => {
        group.customContents.forEach(item => {
          if (item.displayId === originScreen) allOriginContents.push(item);
          if (item.displayId === destinationScreen && !contentDisplayIsSuspended(item)) {
            allDestinationContents.push(item);
          }
        });
      });

      if (allOriginContents.length === 0 || allDestinationContents.length === 0) return;

      const { totalCredit, impressionChanges: originChanges } = transfer.calcCredit(
        allOriginContents,
        transferPercentage,
        calcCreditOptions,
      );

      originChanges.forEach((value, key) => impressionChanges.set(key, value));
      const totalSubsidizedCredit = extractSubsidizedFromOrigins(allOriginContents, originChanges, subsidizedChanges);

      if (totalCredit > 0) {
        const destChanges = transfer.distributeCredit(totalCredit, allDestinationContents, {
          getCpm: (content) => content.smart_campaign_cpm,
          getValue: getCurrentShows,
        });
        destChanges.forEach((value, key) => impressionChanges.set(key, value));
        distributeSubsidizedToDestinations(allDestinationContents, destChanges, totalSubsidizedCredit, subsidizedChanges);
      }
    }

    if (impressionChanges.size === 0) return;

    const updated = contentsDisplaysSelect.map(group => ({
      ...group,
      customContents: group.customContents.map(item => {
        if (!impressionChanges.has(item.relationId) && !subsidizedChanges.has(item.relationId)) return item;
        return {
          ...item,
          ...(impressionChanges.has(item.relationId) && { promised_shows_update: impressionChanges.get(item.relationId) }),
          ...(subsidizedChanges.has(item.relationId) && { promised_shows_subsidized_update: subsidizedChanges.get(item.relationId) }),
          promised_shows_origin: item.promised_shows_origin !== undefined
            ? item.promised_shows_origin
            : Math.max(0, calculateImpressionsView(item)),
        };
      }),
    }));

    setContentsDisplaysSelect(updated);
    refreshCustomContentsAux(updated);
  };

  const updateImpressions = (value, relationId) => {
    const valueAux = parseInt(value, 10);
    const updated = contentsDisplaysSelect.map(group => ({
      ...group,
      customContents: group.customContents.map(item => {
        if (item.relationId !== relationId) return item;

        const currentShows = parseInt(
          (item.promised_shows_update === 0 || item.promised_shows_update !== undefined)
            ? item.promised_shows_update
            : calculateImpressionsView(item),
        );

        // Uses spots.toBudget instead of calculateValueForPrice

        if (valueAux > currentShows) {
          setCreditAvailableFormat(
            creditAvailable - spots.toBudget(valueAux - currentShows, item.smart_campaign_cpm),
          );
        }
        if (valueAux < currentShows) {
          const credit = spots.toBudget(currentShows - valueAux, item.smart_campaign_cpm);
          if (credit > 0) setCreditAvailableFormat(creditAvailable + credit);
        }

        // Subsidized shows are derived from the ORIGINAL ratio (not _update values)
        // so multiple incremental onChange calls converge to the same result.
        const originalPaid = Math.max(0, calculateImpressionsView(item));
        const originalSubsidized = Math.max(0, calculateSubsidizedShows(item));
        const prevSubsidized = (item.promised_shows_subsidized_update === 0 || item.promised_shows_subsidized_update !== undefined)
          ? parseInt(item.promised_shows_subsidized_update)
          : originalSubsidized;

        let newSubsidizedShows = prevSubsidized;
        if (originalPaid > 0 && originalSubsidized > 0) {
          newSubsidizedShows = Math.max(0, Math.round((valueAux / originalPaid) * originalSubsidized));
          const subDelta = newSubsidizedShows - prevSubsidized;
          if (subDelta !== 0) {
            setSubsidizedCreditAvailableFormat(
              subsidizedCreditAvailable - spots.toBudget(subDelta, item.smart_campaign_cpm),
            );
          }
        }

        return {
          ...item,
          promised_shows_update: valueAux,
          promised_shows_subsidized_update: newSubsidizedShows,
          promised_shows_origin: item.promised_shows_origin || Math.max(0, calculateImpressionsView(item)),
        };
      }),
    }));

    setContentsDisplaysSelect(updated);
    refreshCustomContentsAux(updated);
  };

  /**
   * Assign max possible spots to a record given available credit.
   * Uses spots.fromBudget from investmentCalculations.
   */
  const updateImpressionsToMax = (relationId) => {
    const updated = contentsDisplaysSelect.map(group => ({
      ...group,
      customContents: group.customContents.map(item => {
        if (item.relationId !== relationId) return item;

        const costPerImpression = item.smart_campaign_cpm / 1000;
        const maxImpressions = spotsAffordable(creditAvailable, item.smart_campaign_cpm);
        if (maxImpressions < 1) return item;

        const currentShows = parseInt(
          (item.promised_shows_update === 0 || item.promised_shows_update !== undefined)
            ? item.promised_shows_update
            : calculateImpressionsView(item),
        );

        const currentShowsSubsidized = parseInt(
          (item.promised_shows_subsidized_update === 0 || item.promised_shows_subsidized_update !== undefined)
            ? item.promised_shows_subsidized_update
            : calculateSubsidizedShows(item),
        );

        setCreditAvailableFormat(creditAvailable - (maxImpressions * costPerImpression));

        const maxSubsidizedImpressions = spotsAffordable(
          subsidizedCreditAvailable,
          item.smart_campaign_cpm,
        );
        if (maxSubsidizedImpressions > 0) {
          setSubsidizedCreditAvailableFormat(subsidizedCreditAvailable - (maxSubsidizedImpressions * costPerImpression));
        }

        return {
          ...item,
          promised_shows_update: maxImpressions + currentShows,
          promised_shows_subsidized_update: currentShowsSubsidized + maxSubsidizedImpressions,
          promised_shows_origin: item.promised_shows_origin || Math.max(0, calculateImpressionsView(item)),
        };
      }),
    }));

    setContentsDisplaysSelect(updated);
    refreshCustomContentsAux(updated);
  };

  const findContentByRelationId = (relationId) => {
    for (const group of contentsDisplaysSelect) {
      const found = group.customContents.find(i => i.relationId === relationId);
      if (found) return found;
    }
    return null;
  };

  /**
   * Convert a budget input value to spots and update the record.
   * Uses spots.fromBudget from investmentCalculations.
   */
  const updateBudget = (newBudgetValue, relationId) => {
    const item = findContentByRelationId(relationId);
    if (!item || !item.smart_campaign_cpm) return;

    const numeric = typeof newBudgetValue === 'string'
      ? parseFloat(newBudgetValue.replace(/[^\d,.-]/g, '').replace(',', '.'))
      : newBudgetValue;
    if (isNaN(numeric)) return;

    // Only an upper bound: this field must be able to zero out a record's budget
    // even on displays with a volume-discount / minimum-investment floor.
    const requested = spots.fromBudget(numeric, item.smart_campaign_cpm);
    const maxSpots = Math.max(0, limitAddImpressions(item));
    const clamped = Math.min(Math.max(requested, 0), maxSpots);

    updateImpressions(clamped, relationId);
  };

  /**
   * Maximum budget a record can hold: the affordable spot ceiling expressed in
   * currency. Mirrors limitAddImpressions so the budget input and the spots
   * input share a single source of truth for the cap.
   */
  const getMaxBudgetForRecord = (item) => {
    // No CPM: no budget change is possible anyway (updateBudget bails out), so
    // report "no cap" instead of 0 — a 0 cap would make antd snap the input down.
    if (!item || !item.smart_campaign_cpm) return Infinity;
    return spots.toBudget(Math.max(0, limitAddImpressions(item)), item.smart_campaign_cpm);
  };

  /**
   * Calculate the budget amount for a record.
   * Uses spots.toBudget from investmentCalculations.
   */
  const calculateBudget = (origin, display) => {
    const impression =
      origin === 'origin' || (origin === 'no_origin' && display.promised_shows_update === undefined)
        ? calculateImpressionsView(display)
        : display.promised_shows_update;
    return spots.toBudget(impression, display.smart_campaign_cpm).toFixed(2);
  };

  /**
   * Compute added/subtracted spot diff for a record.
   * Delegates to transfer.diff from investmentCalculations.
   */
  const calculateDiffValue = (type, content_display) => transfer.diff(type, content_display);

  /**
   * Returns the number of spots already consumed for a given content-display record.
   * Consumed = promised_shows − remaining (i.e. the `shows` field in the report).
   */
  // const calculateConsumedImpressions = useCallback((item) => {
  //   if (!reportSingleData || !reportSingleData.report) return 0;
  //   const entry = reportSingleData.report.find(
  //     e => e.display === item.displayId && e.content === item.content.id,
  //   );
  //   return entry ? entry.shows : 0;
  // }, [reportSingleData]);
  const getAllDisplayContentsDisplays = useCallback((displayId) =>
    contentsDisplaysSelect.flatMap(g =>
      g.customContents.filter(c => c.displayId === displayId),
    ),
  [contentsDisplaysSelect]);

  const getDisplayAppliedDiscount = (displayId) => {
    const contents = getAllDisplayContentsDisplays(displayId);
    const found = contents
      .map(c => c.appliedDiscount)
      .find(d => d && d.min_impressions);
    return found || null;
  };
  
  const getMinSpotsForRecord = (record) => {
    const allDisplayContents = contentsDisplaysSelect.flatMap(g =>
      g.customContents.filter(c => c.displayId === record.displayId),
    );
    return transfer.minSpotsForRecord(record, allDisplayContents, {
      display: {
        minimum_programmatic_spots: record.minimum_programmatic_spots,
        minimum_programmatic_period: record.minimum_programmatic_period,
      },
      campaignDays: getRelationShowDays(
        currentCampaign.start_date,
        currentCampaign.end_date,
        record.rules,
      ),
    });
  };

  const getRecordFloorReasons = (record) => {
    const appliedDiscount = getDisplayAppliedDiscount(record.displayId);

    const reasons = {
      hasDiscount: !!(appliedDiscount && appliedDiscount.min_impressions),
      hasMinimum: !!record.minimum_programmatic_spots,
    };
    return { ...reasons, appliedDiscount };
  };

  const checkCreditAvailable = () =>
    contentsDisplaysSelect.some(group =>
      group.customContents.some(item => {
        const cpm = parseFloat((item.smart_campaign_cpm / 1000).toFixed(DECIMAL_FOR_CHECK));
        const credit = parseFloat(creditAvailable.toFixed(DECIMAL_FOR_CHECK));
        return cpm !== 0 ? cpm <= credit : false;
      }),
    );

  const limitAddImpressions = (item) => {
    const fallback = (item.promised_shows_update || item.promised_shows_update === 0)
      ? item.promised_shows_update
      : calculateImpressionsView(item);
    const limit = spotsAffordable(creditAvailable, item.smart_campaign_cpm);
    if (limit < 1) return fallback;
    // `!== undefined` (not a truthy check): promised_shows_origin can legitimately
    // be 0, and treating that as "untouched" would understate the ceiling.
    return item.promised_shows_origin !== undefined
      && item.promised_shows_origin !== item.promised_shows_update
      ? item.promised_shows_update + limit
      : calculateImpressionsView(item) + limit;
  };

  /**
   * Commit all pending changes from the local session to the parent.
   * This is the ONLY point where the hook's local state crosses into Redux/parent.
   */
  const updateImpressionsCustomContents = () => {
    contentsDisplaysSelect.forEach(group => {
      group.customContents.forEach(item => {
        if (item.promised_shows_update !== undefined) {
          const addMax = calculateDiffValue('add', item);
          const reportEntry = reportSingleData.report
            ? reportSingleData.report.find(e => e.display === item.displayId && e.content === item.content.id)
            : null;
          const reportShows = reportEntry ? reportEntry.shows : 0;

          // Split consumed proportionally between paid and subsidized.
          // Uses Math.floor to mirror calculateImpressions in src/services/display.js,
          // which is the source of the value displayed in the input — keeps reads and commits aligned.
          const total = item.promised_shows + (item.promised_shows_subsidized || 0);
          const paidConsumed = total > 0
            ? Math.min(item.promised_shows, Math.floor(reportShows * item.promised_shows / total))
            : Math.min(item.promised_shows, reportShows);
          const subsidizedConsumed = total > 0
            ? Math.min(item.promised_shows_subsidized || 0, Math.floor(reportShows * (item.promised_shows_subsidized || 0) / total))
            : 0;
          const totalConsumed = paidConsumed + subsidizedConsumed;

          const impressionUpdate = item.promised_shows_update + paidConsumed;

          let subsidizedUpdate;
          if (item.promised_shows_subsidized_update !== undefined) {
            subsidizedUpdate = item.promised_shows_subsidized_update + subsidizedConsumed;
          }

          updateImpressionsDisplay(item.displayId, item.relationId, impressionUpdate, item.promised_shows_update, addMax, subsidizedUpdate, totalConsumed);
        } else {
          updateImpressionsDisplay(item.displayId, item.relationId, item.promised_shows, 0);
        }
      });
    });

    showTransferModal();
  };

  return {
    // State (read-only for component)
    contentsDisplaysSelect,
    view,
    displayOptions,
    displayFilter,
    creditAvailable,
    subsidizedCreditAvailable,
    adVersions,
    originVersion,
    destinationVersion,
    originScreen,
    destinationScreen,
    transferPercentage,
    format,
    // State setters (for controlled inputs)
    setTransferPercentage,
    setOriginVersion,
    setDestinationVersion,
    setOriginScreen,
    setDestinationScreen,
    // Event handlers
    handleRadioChange,
    handleTransfer,
    handleChangeDisplayFilter,
    handleSearchDisplayFilter,
    updateImpressions,
    updateImpressionsToMax,
    updateBudget,
    updateImpressionsCustomContents,
    // Calculation helpers (passed down to AdViewComponent / DisplayViewComponent)
    calculateBudget,
    getMaxBudgetForRecord,
    calculateDiffValue,
    calculateImpressionsView,
    getCurrentSubsidizedShows,
    // calculateConsumedImpressions,
    getMinSpotsForRecord,
    getRecordFloorReasons,
    checkCreditAvailable,
    limitAddImpressions,
  };
}

export default useBudgetTransfer;
